
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Star, Quote } from "lucide-react";

const stories = [
  {
    clientName: "Sarah Johnson",
    clientRole: "Small Business Owner",
    providerName: "Alex Chen",
    providerSkill: "Web Development",
    project: "E-commerce Website",
    rating: 5,
    testimonial: "Alex delivered an amazing e-commerce website that exceeded my expectations. The attention to detail and communication throughout the project was outstanding.",
    result: "300% increase in online sales within first month",
    image: "https://images.unsplash.com/photo-1494790108755-2616b612b5bb?auto=format&fit=crop&q=80&w=100"
  },
  {
    clientName: "Michael Torres",
    clientRole: "Marketing Manager",
    providerName: "Emma Rodriguez",
    providerSkill: "Graphic Design",
    project: "Brand Identity Package",
    rating: 5,
    testimonial: "Emma created a stunning brand identity that perfectly captured our company's vision. Her creativity and professionalism were exceptional.",
    result: "Brand recognition increased by 85%",
    image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=100"
  },
  {
    clientName: "David Park",
    clientRole: "Startup Founder",
    providerName: "Maria Silva",
    providerSkill: "Content Writing",
    project: "Website Content & Blog",
    rating: 5,
    testimonial: "Maria's writing elevated our entire brand voice. Her ability to understand our audience and create engaging content was remarkable.",
    result: "Website traffic doubled in 3 months",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=100"
  },
  {
    clientName: "Lisa Wang",
    clientRole: "Event Coordinator",
    providerName: "James Miller",
    providerSkill: "Photography",
    project: "Corporate Event Coverage",
    rating: 5,
    testimonial: "James captured our corporate event beautifully. His professionalism and eye for detail made our event memorable for all attendees.",
    result: "Client satisfaction score: 98%",
    image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&q=80&w=100"
  }
];

const stats = [
  { number: "10,000+", label: "Successful Projects" },
  { number: "98%", label: "Client Satisfaction" },
  { number: "5,000+", label: "Active Providers" },
  { number: "24hr", label: "Average Response Time" }
];

const SuccessStories = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="font-poppins font-bold text-4xl md:text-5xl mb-4">Success Stories</h1>
          <p className="font-inter text-xl text-gray-600 max-w-3xl mx-auto">
            Real stories from clients and providers who achieved amazing results through our platform.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-16">
          {stats.map((stat, index) => (
            <Card key={index} className="text-center">
              <CardContent className="pt-6">
                <div className="font-poppins font-bold text-3xl text-purple-600 mb-2">{stat.number}</div>
                <div className="font-inter text-gray-600">{stat.label}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="space-y-8">
          {stories.map((story, index) => (
            <Card key={index} className="overflow-hidden">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-4">
                    <img 
                      src={story.image} 
                      alt={story.clientName} 
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div>
                      <CardTitle className="font-poppins text-xl">{story.project}</CardTitle>
                      <CardDescription className="font-inter">
                        {story.clientName} • {story.clientRole}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant="secondary">{story.providerSkill}</Badge>
                    <div className="flex items-center space-x-1">
                      {[...Array(story.rating)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2">
                    <div className="flex items-start space-x-3">
                      <Quote className="h-6 w-6 text-gray-400 flex-shrink-0 mt-1" />
                      <p className="font-inter text-gray-700 italic">{story.testimonial}</p>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="font-inter text-sm text-gray-600">
                        Provider: <span className="font-semibold">{story.providerName}</span>
                      </p>
                    </div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="font-poppins font-semibold text-green-800 mb-2">Result</h4>
                    <p className="font-inter text-green-700">{story.result}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SuccessStories;
